export interface MomentHighlight {
  time: string;
  desc: string;
}

export interface MomentSection {
  heading: string;
  paragraphs: string[];
}

export interface Moment {
  id: string;
  title: string;
  category: string;
  tags: string[];
  summary: string;
  cover: string;
  date: string;
  views: number;
  likes: number;
  highlights: MomentHighlight[];
  sections: MomentSection[];
}

export const momentCategories = [
  '全部',
  '综艺名场面',
  '舞台高光',
  '直播抓拍',
  '采访神回复',
  '红毯瞬间',
  '幕后花絮',
];

export const moments: Moment[] = [
  {
    id: 'studio-mic-hot-three-seconds',
    title: '综艺直播名场面：麦克风没关的那三秒',
    category: '综艺名场面',
    tags: ['私拍吃瓜名场面', '直播抓拍', '临场反应'],
    summary:
      '短短三秒的收音事故，让整期综艺从平淡直接冲上热搜。我们把它拆成三段关键画面，方便你一眼定位最出戏的那一帧。',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20orange%20television%20studio%20with%20soft%20spotlights%20and%20visible%20haze%2C%20abstract%20microphone%20silhouette%20on%20a%20desk%2C%20blurred%20bokeh%20lights%2C%20cinematic%20editorial%20poster%20style%2C%20no%20people%2C%20cozy%20amber%20tones&width=800&height=600&seq=gt-studio-mic&orientation=landscape',
    date: '2026-09-08',
    views: 182400,
    likes: 9640,
    highlights: [
      { time: '00:41', desc: '镜头切远景的瞬间，台下收音里出现了一句完全不在台本里的吐槽。' },
      { time: '00:44', desc: '主持人愣住的半秒，随后用一句自嘲把场面拉了回来。' },
      { time: '00:52', desc: '后台工作人员的手势被镜头挡住，成为全场最好笑的一帧。' },
    ],
    sections: [
      {
        heading: '为什么这三秒值得反复看',
        paragraphs: [
          '整段内容最妙的地方不在事故本身，而在于现场没有人慌乱。镜头、收音、主持人的节奏几乎同时慢了一拍，然后被一句话重新接管。',
          '我们把这段画面按时间轴拆开，是因为它的笑点非常集中——如果你只是想找那个表情，直接跳到 00:44 就够了。',
        ],
      },
      {
        heading: '现场气氛是怎么被救回来的',
        paragraphs: [
          '真正的高手不是不出错，而是出错之后那半秒的反应。主持人先承认、再自嘲、最后把话题抛回给嘉宾，三步走完，观众甚至以为这是设计好的桥段。',
          '这也是我们认为它属于「神操作」而不是「翻车」的原因：翻车看的是尴尬，神操作看的是掌控力。',
        ],
      },
      {
        heading: '值得记录的细节',
        paragraphs: [
          '注意台边两位嘉宾的眼神交流，以及远景里工作人员的反应，这些没有字幕的细节往往比正片更耐看。',
        ],
      },
    ],
  },
  {
    id: 'stage-encore-one-spotlight',
    title: '舞台高光：一束追光如何救回整场返场',
    category: '舞台高光',
    tags: ['高光瞬间', '舞台名场面', '灯光设计'],
    summary:
      '返场环节的灯光本该收尾，一束没按时跟上来的追光，意外造就了全场最安静也最有力的十秒。',
    cover:
      'https://readdy.ai/api/search-image?query=Single%20warm%20white%20spotlight%20beam%20cutting%20through%20dark%20stage%20haze%2C%20abstract%20concert%20lighting%20design%2C%20dust%20particles%20in%20air%2C%20cinematic%20minimal%20composition%2C%20no%20people%2C%20amber%20and%20rose%20highlights&width=800&height=600&seq=gt-stage-spotlight&orientation=landscape',
    date: '2026-09-07',
    views: 156200,
    likes: 8830,
    highlights: [
      { time: '01:12', desc: '伴奏提前收尾，现场短暂安静，追光却没有立刻亮起。' },
      { time: '01:18', desc: '追光从侧台扫进来的那一刻，观众的欢呼才真正爆开。' },
      { time: '01:22', desc: '演员站在原地没有动，把这个意外变成了故意的留白。' },
    ],
    sections: [
      {
        heading: '一个「失误」为什么成了高光',
        paragraphs: [
          '舞台灯光和音乐的对位，是返场环节的呼吸感来源。当音乐先一步落下，短暂的空拍反而给了观众反应的空间。',
          '这十秒被反复截图，是因为它证明了一件事：高光不一定来自整齐划一，也可以来自一次恰到好处的错位。',
        ],
      },
      {
        heading: '从灯光角度复盘',
        paragraphs: [
          '追光延迟进入，客观上完成了「先听见欢呼、再看见人」的顺序。这个顺序在舞台上非常昂贵，因为它需要观众先投入情绪。',
          '后来的返场都刻意保留了这个延迟，可见现场团队也意识到，这是一次值得复制的意外。',
        ],
      },
    ],
  },
  {
    id: 'livestream-slip-catch',
    title: '直播抓拍：一口误之后的三连救场',
    category: '直播抓拍',
    tags: ['私拍吃瓜抓拍', '神操作', '直播'],
    summary:
      '口误、卡顿、弹幕刷屏，三个雷点在同一条直播里连成一串。主播用三句话把它们变成了当晚的流量高点。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20streaming%20desk%20setup%20with%20ring%20light%20glow%2C%20warm%20amber%20and%20rose%20neon%20reflections%2C%20blurred%20computer%20screen%20bokeh%2C%20minimal%20cinematic%20editorial%20style%2C%20no%20people%2C%20cozy%20studio%20atmosphere&width=800&height=600&seq=gt-livestream-desk&orientation=landscape',
    date: '2026-09-06',
    views: 143800,
    likes: 7120,
    highlights: [
      { time: '00:23', desc: '一句口误刚落，弹幕瞬间刷满，评论区直接失控。' },
      { time: '00:27', desc: '主播没有删掉重来，而是顺着口误编了个梗。' },
      { time: '00:35', desc: '第二波卡顿袭来，画面卡在半句话上，反而成了名场面。' },
    ],
    sections: [
      {
        heading: '直播和录播最大的区别',
        paragraphs: [
          '录播可以重来，直播不能。所以直播里的名场面，本质上都是「无法重来」这四个字的注脚。',
          '这条片段之所以被反复搬运，是因为它在一分钟内把三种意外叠在了一起，而主播一次都没有按下结束键。',
        ],
      },
      {
        heading: '弹幕为什么会反过来帮忙',
        paragraphs: [
          '当主播放下面子、主动把口误念出来的时候，弹幕的情绪从「抓错」变成了「一起玩梗」。这是直播里最典型的情绪转向。',
        ],
      },
      {
        heading: '关键画面的定位建议',
        paragraphs: [
          '如果你想看最出戏的部分，直接跳到 00:35。那一刻画面卡顿、声音还在继续，是整段最魔幻的两秒。',
        ],
      },
    ],
  },
  {
    id: 'interview-one-line-ends',
    title: '采访神回复：一句话终结连环追问',
    category: '采访神回复',
    tags: ['采访名场面', '神操作', '高情商'],
    summary:
      '记者连问三轮，问题越来越尖锐。被采访者只用一句话，就把整场采访的节奏握回自己手里。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20press%20interview%20scene%20with%20handheld%20microphone%20silhouette%2C%20warm%20amber%20backlight%2C%20soft%20blurred%20background%20of%20lens%20flares%2C%20cinematic%20editorial%20photography%20style%2C%20no%20people%2C%20minimal%20composition&width=800&height=600&seq=gt-interview-mic&orientation=landscape',
    date: '2026-09-05',
    views: 132600,
    likes: 6980,
    highlights: [
      { time: '00:05', desc: '第一个问题就直奔敏感点，现场空气肉眼可见地紧了一下。' },
      { time: '00:31', desc: '连续追问到第三轮时，被采访者停顿了两秒。' },
      { time: '00:34', desc: '一句轻描淡写的回答，让记者当场笑了出来，追问就此结束。' },
    ],
    sections: [
      {
        heading: '神回复的底层逻辑',
        paragraphs: [
          '真正高明的回复，不是把问题挡回去，而是让对方心甘情愿地换一个问题。这句话做到了后者。',
          '它没有回避，也没有正面硬碰，而是把一个尖锐问题还原成一个日常话题，锋利感瞬间消失。',
        ],
      },
      {
        heading: '为什么记者反而笑了',
        paragraphs: [
          '因为这句话既给了台阶，又守住了底线。在采访场景里，这种「双赢」的收尾非常少见。',
        ],
      },
    ],
  },
  {
    id: 'redcarpet-wind-moment',
    title: '红毯瞬间：风把裙摆吹起来之后',
    category: '红毯瞬间',
    tags: ['红毯名场面', '抓拍', '临场反应'],
    summary:
      '红毯上的风永远不配合。这次它掀起的不只是裙摆，还有一段被摄影师连拍的经典画面。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20red%20carpet%20walkway%20with%20gold%20confetti%20and%20soft%20wind%20blown%20fabric%20shapes%2C%20warm%20amber%20spotlights%2C%20blurred%20photographer%20flashes%20bokeh%2C%20cinematic%20editorial%20style%2C%20no%20people&width=800&height=600&seq=gt-redcarpet-wind&orientation=landscape',
    date: '2026-09-04',
    views: 121500,
    likes: 6430,
    highlights: [
      { time: '—', desc: '第一阵风来的瞬间，连拍快门声明显变密。' },
      { time: '—', desc: '当事人没有去按裙摆，而是顺势转了个身，把意外变成造型。' },
      { time: '—', desc: '远景里工作人员冲上来的画面，成了花絮区最热门的一张。' },
    ],
    sections: [
      {
        heading: '红毯为什么最容易出抓拍',
        paragraphs: [
          '红毯是一个「不能停」的场景：往前走、不能回头、灯光密集。所有条件都在逼出最真实的反应。',
          '这次的名场面之所以好看，是因为当事人的处理方式非常松弛，没有出现任何慌乱的定格。',
        ],
      },
      {
        heading: '值得注意的画面细节',
        paragraphs: [
          '留意裙摆的走向和风的方向其实是相反的——说明她主动做了一个反向的转身，这是经验。',
        ],
      },
    ],
  },
  {
    id: 'backstage-five-relaxed-minutes',
    title: '幕后花絮：后台最松弛的五分钟',
    category: '幕后花絮',
    tags: ['幕后花絮', '私拍吃瓜', '反差感'],
    summary:
      '镜头一转进后台，紧绷了一整晚的人突然全部松了下来。这五分钟没有任何台词，却比正片更好看。',
    cover:
      'https://readdy.ai/api/search-image?query=Backstage%20dressing%20room%20with%20warm%20vanity%20mirror%20bulbs%2C%20abstract%20costume%20racks%20and%20soft%20amber%20light%2C%20cozy%20blurred%20bokeh%2C%20cinematic%20editorial%20style%2C%20no%20people%2C%20warm%20neutral%20palette&width=800&height=600&seq=gt-backstage-mirror&orientation=landscape',
    date: '2026-09-03',
    views: 110400,
    likes: 5720,
    highlights: [
      { time: '—', desc: '门一关上，所有人同时叹了口气，气氛瞬间松弛。' },
      { time: '—', desc: '有人把高跟鞋脱了拎在手里，被镜头完整拍到。' },
      { time: '—', desc: '最后一个片段是大家一起看回放，边看边笑出声。' },
    ],
    sections: [
      {
        heading: '反差感是花絮的通行证',
        paragraphs: [
          '台前的精致和台后的真实，天然形成对比。这种对比不需要任何剪辑技巧，直接放出来就好看。',
          '这也是我们把它单独归类到「幕后花絮」的原因：它不靠冲突，靠的是松弛。',
        ],
      },
      {
        heading: '为什么花絮反而更容易出圈',
        paragraphs: [
          '正片需要配合节目节奏，花絮可以留白。观众在花絮里看到的是「人」，而不是「角色」。',
        ],
      },
    ],
  },
  {
    id: 'award-wrong-name-silence',
    title: '颁奖礼尴尬瞬间：念错名字之后的三秒沉默',
    category: '综艺名场面',
    tags: ['尴尬瞬间', '颁奖礼', '临场反应'],
    summary:
      '台本上的名字和念出来的名字不一样。三秒沉默之后，全场用掌声化解了这场尴尬。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20award%20ceremony%20stage%20with%20golden%20trophy%20silhouette%20and%20warm%20spotlights%2C%20deep%20amber%20and%20rose%20tones%2C%20blurred%20confetti%20bokeh%2C%20cinematic%20editorial%20poster%20style%2C%20no%20people&width=800&height=600&seq=gt-award-trophy&orientation=landscape',
    date: '2026-09-02',
    views: 99800,
    likes: 5140,
    highlights: [
      { time: '02:06', desc: '名字念出口的瞬间，台下有几个人同时抬起了头。' },
      { time: '02:09', desc: '三秒沉默，导播甚至没有切走镜头。' },
      { time: '02:14', desc: '另一位提名者先鼓掌，全场才跟着响起来。' },
    ],
    sections: [
      {
        heading: '尴尬瞬间为什么让人记住',
        paragraphs: [
          '因为它是真实发生的。所有台本、走位、提词器在这三秒里全部失效，只剩下现场的人。',
          '这类片段在「私拍吃瓜尴尬瞬间」里永远排得很前，不是因为它好笑，而是因为它足够真。',
        ],
      },
      {
        heading: '化解尴尬的那一下掌声',
        paragraphs: [
          '最先鼓掌的人往往决定了现场的情绪走向。这一次，掌声来得比任何救场台词都有效。',
        ],
      },
    ],
  },
  {
    id: 'variety-prop-domino',
    title: '综艺游戏翻车：道具掉落的连锁反应',
    category: '综艺名场面',
    tags: ['神操作', '综艺', '连锁反应'],
    summary:
      '一个道具掉在地上，接着是第二个、第三个。全场在十秒内彻底收不住，导演组直接放弃喊停。',
    cover:
      'https://readdy.ai/api/search-image?query=Colorful%20playful%20variety%20show%20set%20with%20scattered%20props%20and%20confetti%20mid%20air%2C%20warm%20orange%20and%20yellow%20stage%20lighting%2C%20abstract%20blurred%20shapes%2C%20cinematic%20editorial%20style%2C%20no%20people&width=800&height=600&seq=gt-variety-props&orientation=landscape',
    date: '2026-09-01',
    views: 91200,
    likes: 4760,
    highlights: [
      { time: '00:18', desc: '第一个道具滑落，大家还在努力维持流程。' },
      { time: '00:21', desc: '第二个道具被带倒，现场开始有笑声。' },
      { time: '00:26', desc: '导演组放弃喊停，镜头干脆推了个大远景。' },
    ],
    sections: [
      {
        heading: '翻车为什么比通关好看',
        paragraphs: [
          '游戏类内容一旦顺利通关，观众猜得到结局；翻车不可预测，所以更有观看价值。',
          '这条片段的笑点在于「连锁」——每一次掉落都比上一次更离谱，节奏完全停不下来。',
        ],
      },
      {
        heading: '导演组的选择',
        paragraphs: [
          '不喊停，是对现场喜剧感最尊重的做法。这个决定让原本可能被剪掉的十五秒完整保留了下来。',
        ],
      },
    ],
  },
  {
    id: 'concert-crowd-louder',
    title: '演唱会名场面：观众合唱盖过了伴奏',
    category: '舞台高光',
    tags: ['高光瞬间', '合唱', '现场'],
    summary:
      '副歌响起的那一刻，伴奏几乎被观众的声音淹没。台上的返听里，只剩下密密麻麻的人声。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20concert%20arena%20with%20thousands%20of%20warm%20amber%20and%20rose%20light%20sticks%2C%20blurred%20bokeh%20sea%20of%20lights%2C%20hazy%20atmosphere%2C%20cinematic%20editorial%20poster%20style%2C%20no%20people%20faces&width=800&height=600&seq=gt-concert-crowd&orientation=landscape',
    date: '2026-08-30',
    views: 87400,
    likes: 4380,
    highlights: [
      { time: '01:44', desc: '副歌前奏一响，全场灯光棒同时亮起。' },
      { time: '01:50', desc: '人声明显盖过伴奏，调音台现场把伴奏推低。' },
      { time: '02:02', desc: '台上的人摘了一只耳返，就为了听清观众的声音。' },
    ],
    sections: [
      {
        heading: '一个被声音记录的瞬间',
        paragraphs: [
          '这一幕真正的证据在音频里：伴奏被推低的那两秒，观众的声浪反而更完整。',
          '很多年后大家记住的不是哪一句歌词，而是「全场一起唱」的那种震感。',
        ],
      },
      {
        heading: '为什么它属于高光',
        paragraphs: [
          '因为它是被观众共同完成的。舞台上的高光通常靠一个人，这一次靠的是几万人。',
        ],
      },
    ],
  },
  {
    id: 'cook-live-flip-chaos',
    title: '美食直播抓拍：翻锅一秒变喜剧',
    category: '直播抓拍',
    tags: ['抓拍', '直播', '搞笑瞬间'],
    summary:
      '一次看起来非常熟练的翻锅，最后变成了一场厨房里的即兴喜剧。弹幕从「好帅」瞬间切换到「没事吧」。',
    cover:
      'https://readdy.ai/api/search-image?query=Abstract%20warm%20kitchen%20counter%20with%20rising%20steam%20and%20soft%20amber%20lighting%2C%20blurred%20cookware%20shapes%2C%20cozy%20cinematic%20editorial%20food%20photography%20style%2C%20no%20people%2C%20warm%20neutral%20palette&width=800&height=600&seq=gt-cook-live&orientation=landscape',
    date: '2026-08-28',
    views: 79600,
    likes: 3920,
    highlights: [
      { time: '00:07', desc: '锅一抖，食材完美地飞向镜头外的方向。' },
      { time: '00:09', desc: '镜头拍到空锅，弹幕从好帅变成没事吧。' },
      { time: '00:15', desc: '主播笑着把地上的东西捡起来，决定重来一次。' },
    ],
    sections: [
      {
        heading: '生活直播的魅力',
        paragraphs: [
          '厨房场景之所以容易出名场面，是因为它同时具备了「有风险」和「有烟火气」两个条件。',
          '这一条没有任何剧本，它的全部笑点都来自一次真实的失败。',
        ],
      },
      {
        heading: '重来一次的勇气',
        paragraphs: [
          '最有意思的是收尾：主播没有剪辑掉这一段，而是当着所有人重做了一遍，并且成功了。',
        ],
      },
    ],
  },
  {
    id: 'press-junket-cold-silence',
    title: '发布会冷场：主持人紧急圆场的 20 秒',
    category: '采访神回复',
    tags: ['采访名场面', '圆场', '神操作'],
    summary:
      '一个问题抛出去，台下安静得能听见空调声。主持人接话的二十秒，成了整场发布会最精彩的部分。',
    cover:
      'https://readdy.ai/api/search-image?query=Minimal%20press%20conference%20podium%20with%20warm%20amber%20accent%20lighting%2C%20abstract%20microphone%20and%20curtain%20shapes%2C%20soft%20blurred%20bokeh%2C%20cinematic%20editorial%20style%2C%20no%20people&width=800&height=600&seq=gt-press-podium&orientation=landscape',
    date: '2026-08-26',
    views: 71400,
    likes: 3460,
    highlights: [
      { time: '00:00', desc: '问题抛出，全场安静，没有人接话。' },
      { time: '00:08', desc: '主持人笑着把自己也写进了这个问题里。' },
      { time: '00:20', desc: '气氛松动，第二位提问者终于举手。' },
    ],
    sections: [
      {
        heading: '冷场其实很好救',
        paragraphs: [
          '冷场不可怕，可怕的是试图用官方话术硬撑。主持人选择把自己当成笑点的一部分，气氛立刻就活了。',
        ],
      },
      {
        heading: '值得学的圆场结构',
        paragraphs: [
          '自嘲、共情、再给一个具体的邀请。三步走完，沉默自动结束。',
        ],
      },
    ],
  },
  {
    id: 'reunion-ten-years-later',
    title: '重聚高光：十年后再同框的那一瞬',
    category: '幕后花絮',
    tags: ['高光瞬间', '重聚', '情绪'],
    summary:
      '十年没有同框的人，在同一个走廊尽头碰到。没有拥抱，两个人先同时愣住了一秒。',
    cover:
      'https://readdy.ai/api/search-image?query=Warm%20backstage%20corridor%20with%20soft%20amber%20ceiling%20lights%2C%20long%20perspective%20and%20blurred%20depth%2C%20cozy%20nostalgic%20cinematic%20editorial%20style%2C%20no%20people%2C%20gentle%20neutral%20palette&width=800&height=600&seq=gt-reunion-hall&orientation=landscape',
    date: '2026-08-24',
    views: 68900,
    likes: 4210,
    highlights: [
      { time: '—', desc: '两个人在走廊两头同时停下，谁都没有先动。' },
      { time: '—', desc: '愣住的一秒后，其中一个先笑了出来。' },
      { time: '—', desc: '这段没有配乐，只有现场的环境音。' },
    ],
    sections: [
      {
        heading: '为什么没有拥抱更动人',
        paragraphs: [
          '因为真实的久别重逢，通常不是立刻拥抱，而是先确认一遍「是你吗」。那一秒的空白就是全部情绪。',
        ],
      },
      {
        heading: '关于这段画面的取舍',
        paragraphs: [
          '后期本来想加配乐，最后放弃了。没有音乐，环境音的呼吸声反而更清楚。',
        ],
      },
    ],
  },
];